import React, { useEffect, useState, useRef, useCallback, useMemo} from 'react';
import { Box, IconButton } from '@mui/material';
import { PlayArrow, Pause } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { addDays, subDays, addMinutes, addHours, differenceInMinutes } from 'date-fns';
import styled from '@emotion/styled';
import MyDatePicker from '../components/MyDatePicker';


const TimeScaleContainer = styled.div`
  position: relative;
  width: 98%;
  height: ${({ numBands }) => `${36 + numBands * 4}px`};
  overflow: hidden;
  background-color: #f0f0f0;
  user-select: none;
  border-radius: 8px;
`;

const TimeScale = styled.div`
  position: absolute;
  top: 20px
  height: 100%;
  display: flex;
  align-items: center;
`;

const TimeMarker = styled.div`
  width: 2px;
  top: ${({ numBands }) => `${numBands * 4}px`};
  height: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  transform: translateX(-50%); // Center horizontally
`;

const TimeLabel = styled.div`
  position: absolute;
  top: 20px;
  transform: translateX(-50%);
  font-size: 12px;
  font-family: monospace; // Use a monospaced font to ensure consistent character width
  font-weight: bold;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const Pointer = styled.div`
  position: absolute;
  top: 0;
  left: 50%; // Centered horizontally
  transform: translateX(-50%); // Offset by half its width to align perfectly
  width: 2px;
  height: 100%;
  background-color: red;
`;

const CacheBar = styled.div`
  position: absolute;
  height: 4px;
  top: ${({ index }) => `${index * 4}px`}; /* Adjust spacing between bars */
  width: 100%;
  background-color: #ccc;
`;

const GreenBandContainer = styled.div`
  position: absolute;
  top: 0;
  left: ${({ offset, pixelsPerHour }) => `${-offset * (pixelsPerHour / 60)}px`};
  display: flex;
  height: 100%;
  width: ${({ pixelsPerHour }) => `${72 * pixelsPerHour}px`};
`;

const GreenBandSegment = styled.div`
  flex: 1;
  background-color: ${({ isCached, color }) => isCached ? color : 'transparent'};
`;

const Timeline = ({ onDateTimeChange, cachedData = {}, onPlayStateChange, currentDateTime }) => {
    // Convert currentDateTime to a Date object if it's not already
    const currentDateTimeObject = useMemo(() => {
        return currentDateTime instanceof Date ? currentDateTime : new Date(currentDateTime);
    }, [currentDateTime]);
        
    // Initialize selectedDate with the date part (no time) of currentDateTime
    const [selectedDate, setSelectedDate] = useState(() => {
        const date = new Date(currentDateTimeObject);
        date.setUTCHours(0, 0, 0, 0);
        return date;
    });
    
    // Calculate minutes since midnight for currentDateTime
    const calculateMinutesSinceMidnight = (dateTime) => {
        return dateTime.getUTCHours() * 60 + dateTime.getUTCMinutes();
    };
    
    // Initialize currentTime based on the time part of currentDateTime
    const [currentTime, setCurrentTime] = useState(() => 
        calculateMinutesSinceMidnight(currentDateTimeObject)
    );
    
    const [originDate, setOriginDate] = useState(selectedDate);
    const [offset, setOffset] = useState(2000);
    const [isPlaying, setIsPlaying] = useState(false);

    const containerRef = useRef(null);
    const scaleRef = useRef(null);
    const isDragging = useRef(false);
    const lastX = useRef(0);
    const prevDateTime = useRef(currentDateTimeObject);

    // Effect to sync with external currentDateTime changes
    useEffect(() => {
        // Skip if the dates are the same (to avoid infinite loops)
        if (prevDateTime.current.getTime() === currentDateTimeObject.getTime()) {
            return;
        }
        
        // Update prevDateTime ref
        prevDateTime.current = currentDateTimeObject;
        
        // Extract date part (midnight)
        const newDate = new Date(currentDateTimeObject);
        newDate.setUTCHours(0, 0, 0, 0);
        
        // Calculate minutes since midnight
        const newMinutesSinceMidnight = calculateMinutesSinceMidnight(currentDateTimeObject);
        
        // Update state
        setSelectedDate(newDate);
        setCurrentTime(newMinutesSinceMidnight);
    }, [currentDateTimeObject]);

    const updateTimeline = useCallback((newTime) => {
        let updatedTime = newTime;
        let updatedDate = new Date(selectedDate);
        let shouldUpdateOrigin = false;

        while (updatedTime < 0) {
            updatedTime += 1440;
            updatedDate = subDays(updatedDate, 1);
            shouldUpdateOrigin = true;
        }

        while (updatedTime >= 1440) {
            updatedTime -= 1440;
            updatedDate = addDays(updatedDate, 1);
            shouldUpdateOrigin = true;
        }

        if (shouldUpdateOrigin) {
            setSelectedDate(updatedDate);
        }

        return { time: updatedTime, date: updatedDate, shouldUpdateOrigin };
    }, [selectedDate]);

    const updateCurrentDateTime = useCallback(() => {
        const updatedDateTime = addMinutes(selectedDate, currentTime);
        // Avoid triggering an update if the datetime hasn't changed
        if (differenceInMinutes(updatedDateTime, prevDateTime.current) !== 0) {
            prevDateTime.current = updatedDateTime;
            onDateTimeChange(updatedDateTime);
        }
    }, [selectedDate, currentTime, onDateTimeChange]);

    useEffect(() => {
        updateCurrentDateTime();
    }, [selectedDate, currentTime, updateCurrentDateTime]);

    const updateOriginDate = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const pointerX = container.offsetWidth / 2;
        const pointerPosition = pointerX + offset;
        const isPointerInFirstHalf = pointerPosition < 1440;

        setOriginDate(prevOriginDate => {
            const newOriginDate = isPointerInFirstHalf ? selectedDate : subDays(selectedDate, 1);
            return newOriginDate;
        });
    }, [selectedDate, offset]);

    useEffect(() => {
        updateOriginDate();
    }, [selectedDate, offset, updateOriginDate]);

    const updateOffset = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const containerWidth = container.offsetWidth;
        const pixelsPerMinute = 60 / 60; // assuming pixelsPerHour is 60
        const targetOffset = ((currentTime - containerWidth / (2 * pixelsPerMinute)) + 1440) % 1440;

        setOffset(targetOffset);
        if (scaleRef.current) {
            scaleRef.current.style.transform = `translateX(-${targetOffset * pixelsPerMinute}px)`;
        }
    }, [currentTime]);

    useEffect(() => {
        updateOffset();
        window.addEventListener('resize', updateOffset);
        return () => {
            window.removeEventListener('resize', updateOffset);
        };
    }, [updateOffset]);

    const handleMouseDown = useCallback((e) => {
        isDragging.current = true;
        lastX.current = e.clientX;
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging.current) return;

        const delta = Math.round(lastX.current - e.clientX);
        lastX.current = e.clientX;

        if (delta !== 0) {
            const newTime = currentTime + delta;
            const { time: updatedTime, shouldUpdateOrigin } = updateTimeline(newTime);
            setCurrentTime(updatedTime);
            if (shouldUpdateOrigin) {
                updateOriginDate();
            }
        }
    }, [currentTime, updateTimeline, updateOriginDate]);

    const handleMouseUp = useCallback(() => {
        isDragging.current = false;
    }, []);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const togglePlayPause = useCallback(() => {
        setIsPlaying(prev => {
            const newPlayState = !prev;
            onPlayStateChange(newPlayState);  // Call the prop function with the new state
            return newPlayState;
        });
    }, [onPlayStateChange]);

    useEffect(() => {
        let animationFrameId;

        const updateTime = () => {
            if (isPlaying) {
                setCurrentTime(prevTime => {
                    const newTime = prevTime + 1;
                    const { time: updatedTime, shouldUpdateOrigin } = updateTimeline(newTime);
                    if (shouldUpdateOrigin) {
                        updateOriginDate();
                    }
                    return updatedTime;
                });
                animationFrameId = requestAnimationFrame(updateTime);
            }
        };

        if (isPlaying) {
            animationFrameId = requestAnimationFrame(updateTime);
        }

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [isPlaying, updateTimeline, updateOriginDate]);

    const formatDateUTC = useCallback((date) => {
        return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
    }, []);

    const getGreenBandData = useCallback((originDate, layerCache) => {
        // Ensure originDate is in UTC        
        return Array.from({ length: 72 }).map((_, hourIndex) => {
            const targetDateTimeUTC = addHours(originDate, hourIndex);
            const dateStr = formatDateUTC(targetDateTimeUTC);
            const hour = targetDateTimeUTC.getUTCHours();

            // Check if the hour is cached
            const isCached = layerCache[dateStr]?.includes(hour) || false;

            return { hour: hourIndex, isCached };
        });
    }, [formatDateUTC]);

    // Handle date picker changes - preserve the time portion
    const handleDatePickerChange = (date) => {
        // Create a new date with the selected date but keep the current time
        const newDateTime = new Date(selectedDate);
        newDateTime.setUTCFullYear(date.getUTCFullYear());
        newDateTime.setUTCMonth(date.getUTCMonth());
        newDateTime.setUTCDate(date.getUTCDate());
        
        console.log("Date picker changed to:", date.toISOString());
        setSelectedDate(newDateTime);
    };

    const numBands = Object.keys(cachedData).length;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'center' }}>
            <Box sx={{ padding: '8px' }}>
                <MyDatePicker
                    selected={selectedDate}
                    onChange={handleDatePickerChange}
                />
            </Box>
            <IconButton
                onClick={togglePlayPause}
                sx={{
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                    }
                }}
            >
                {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Box sx={{ flex: 1 }}>
                <TimeScaleContainer
                    ref={containerRef}
                    numBands={numBands}
                    onMouseDown={handleMouseDown}
                >
                    <TimeScale ref={scaleRef}>
                        {Array.from({ length: 72 }).map((_, hour) => (
                            <React.Fragment key={hour}>
                                <TimeMarker numBands={numBands} style={{ left: `${hour * 60}px` }} />
                                <TimeLabel style={{ left: `${hour * 60}px` }}>{`${String(hour % 24).padStart(2, '0')}:00`}</TimeLabel>
                            </React.Fragment>
                        ))}
                    </TimeScale>
                    {Object.entries(cachedData || {}).map(([layerId, layerCache], index) => {
                        const greenBandData = getGreenBandData(originDate, layerCache);
                        const color = `hsl(${index * 60}, 70%, 50%)`;
                        return (
                            <CacheBar key={layerId} index={index}>
                                <GreenBandContainer offset={offset} pixelsPerHour={60}>
                                    {greenBandData.map(({ hour, isCached }) => (
                                        <GreenBandSegment key={hour} isCached={isCached} color={color} />
                                    ))}
                                </GreenBandContainer>
                            </CacheBar>
                        );
                    })}
                    <Pointer />
                </TimeScaleContainer>
            </Box>
        </Box>
    );
};

export default Timeline;